import { useState, useMemo } from "react";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useProjectMembers } from "@/hooks/use-project-members";
import { useMembers } from "@/hooks/use-members";

interface MemberListProps {
  projectId: string;
  isAdmin: boolean;
}

export function MemberList({ projectId, isAdmin }: MemberListProps) {
  const { members, add, remove } = useProjectMembers(projectId);
  const { members: orgMembers } = useMembers();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const { members: searchResults } = useMembers({
    search: query,
    enabled: query.length >= 2,
  });
  const results = searchResults.map((m) => ({
    id: m.id,
    name: m.user.name,
    email: m.user.email,
  }));

  const assignedMemberIds = useMemo(() => new Set(members.map((m) => m.memberId)), [members]);

  async function handleAdd(memberId: string) {
    setOpen(false);
    setQuery("");
    try {
      await add.mutateAsync([memberId]);
      toast.success("Member added");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to add member");
    }
  }

  async function handleRemove(memberId: string) {
    try {
      await remove.mutateAsync(memberId);
      toast.success("Member removed");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to remove member");
    }
  }

  const orgMemberMap = useMemo(() => {
    const map = new Map<string, (typeof orgMembers)[0]>();
    for (const om of orgMembers) map.set(om.id, om);
    return map;
  }, [orgMembers]);

  const memberDisplay = useMemo(() =>
    members.map((m) => {
      const orgMember = orgMemberMap.get(m.memberId);
      return {
        ...m,
        name: orgMember?.user?.name,
        email: orgMember?.user?.email,
      };
    }), [members, orgMemberMap]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">Members</h3>
        {isAdmin && (
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm">
                <Plus className="mr-2 h-4 w-4" />
                Add
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[260px] p-0" align="end">
              <Command shouldFilter={false}>
                <CommandInput
                  placeholder="Search members..."
                  value={query}
                  onValueChange={setQuery}
                />
                <CommandList>
                  <CommandEmpty>
                    {query.length < 2 ? "Type to search..." : "No members found."}
                  </CommandEmpty>
                  <CommandGroup>
                    {results
                      .filter((r) => !assignedMemberIds.has(r.id))
                      .map((r) => (
                        <CommandItem
                          key={r.id}
                          value={r.id}
                          onSelect={() => handleAdd(r.id)}
                        >
                          <div>
                            <p className="text-sm font-medium">{r.name}</p>
                            <p className="text-xs text-muted-foreground">{r.email}</p>
                          </div>
                        </CommandItem>
                      ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )}
      </div>

      {memberDisplay.length > 0 ? (
        <div className="divide-y">
          {memberDisplay.map((m) => (
            <div
              key={m.id}
              className="flex items-center justify-between py-2"
            >
              <div className="flex items-center gap-2.5">
                <Avatar className="h-7 w-7">
                  <AvatarFallback className="text-xs">
                    {(m.name ?? m.email ?? "?").slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-sm font-medium">{m.name ?? m.memberId}</p>
                  {m.email && (
                    <p className="text-xs text-muted-foreground">{m.email}</p>
                  )}
                </div>
              </div>
              {isAdmin && (
                <button
                  onClick={() => handleRemove(m.memberId)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No members assigned.</p>
      )}
    </div>
  );
}
